import { useMutation, useQuery } from '@tanstack/react-query'
import {
  DESTINACOES,
  formatarCnpj,
  ROTULO_DESTINACAO,
  ROTULO_TRATAMENTO,
  TRATAMENTOS,
  type Destinacao,
  type PerfilTributario,
  type Tratamento,
} from '@via-tax/contracts'
import { Aviso, Botao, Campo, Entrada, Selecao, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { dataBr, reais } from '../formatos'
import { mensagemDeErro, useTRPC } from '../trpc'
import { Confianca, ROTULO_STATUS } from './RevisaoEstoque'

interface Alternativa {
  origem: string
  cClassTrib: string
  tratamento: string
  fundamento: string
  confianca: number
  condicional: boolean
  observacoes: string[]
}

/** Painel lateral: o que a lei diz, por que, com que confiança — e a assinatura de quem aprova. */
export function DetalheProduto({
  produtoId,
  podeAprovar,
  fechar,
  aoAprovar,
}: {
  produtoId: string
  podeAprovar: boolean
  fechar: () => void
  aoAprovar: () => void
}) {
  const trpc = useTRPC()
  const q = useQuery(trpc.produto.detalhe.queryOptions({ produtoId }))
  const [editando, setEditando] = useState(false)
  const aceitar = useMutation(trpc.produto.aceitarSugestoes.mutationOptions({ onSuccess: () => { aoAprovar(); void q.refetch() } }))

  const d = q.data
  const sugestao = d?.sugestao?.perfil as PerfilTributario | Record<string, never> | undefined
  const temSugestao = Boolean(sugestao && 'cClassTrib' in sugestao)
  const vigente = d?.historico[0]

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={fechar}>
      <aside
        className="flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-painel shadow-xl"
        onClick={(e) => e.stopPropagation()}
        aria-label="Detalhe do produto"
      >
        <header className="sticky top-0 flex items-start justify-between gap-3 border-b border-linha bg-painel px-5 py-4">
          <div className="min-w-0">
            <p className="num text-[11px] text-tinta-3">
              {d?.produto.tipo === 'ENTRADA' ? 'Insumo' : 'Produto'} · {d?.produto.codigoProduto}
            </p>
            <h2 className="text-base font-semibold leading-snug">{d?.produto.descricao ?? 'Carregando…'}</h2>
          </div>
          <Botao variante="fantasma" onClick={fechar} aria-label="Fechar">
            ✕
          </Botao>
        </header>

        {q.isError && <div className="p-5"><Aviso>{mensagemDeErro(q.error)}</Aviso></div>}
        {d && (
          <div className="flex flex-col gap-5 px-5 py-4 text-sm">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <Item rotulo="NCM nas notas">
                <span className="num">{d.produto.ncmAtual ?? '—'}</span>
                {d.produto.qtdNcmDistintos > 1 && <span className="ml-1.5"><Selo tom="alerta">{d.produto.qtdNcmDistintos} NCMs diferentes</Selo></span>}
              </Item>
              <Item rotulo="Situação"><Selo tom="navy">{ROTULO_STATUS[d.produto.statusRevisao]}</Selo></Item>
              <Item rotulo="Descrição oficial do NCM" largo>
                <span className="text-tinta-2">{d.ncmDescricao ?? 'NCM não encontrado na nomenclatura vigente'}</span>
              </Item>
              {d.produto.tipo === 'ENTRADA' && (
                <Item rotulo="Fornecedor" largo>
                  {d.produto.fornecedorNome ?? '—'}{' '}
                  <span className="num text-tinta-3">{d.produto.fornecedorDoc.length === 14 ? formatarCnpj(d.produto.fornecedorDoc) : ''}</span>
                </Item>
              )}
              <Item rotulo="GTIN"><span className="num">{d.produto.gtin ?? '—'}</span></Item>
              <Item rotulo="Unidade · CFOP"><span className="num">{d.produto.unidade ?? '—'} · {d.produto.cfopPrincipal ?? '—'}</span></Item>
              <Item rotulo="Valor no período"><span className="num">{reais(d.produto.valorAcumulado)}</span></Item>
              <Item rotulo="Notas">
                <span className="num">{d.produto.qtdDocumentos}</span>{' '}
                <span className="text-tinta-3">({dataBr(d.produto.primeiroVistoEm)} a {dataBr(d.produto.ultimoVistoEm)})</span>
              </Item>
              <Item rotulo="cClassTrib declarado nas notas"><span className="num">{d.produto.cclasstribDeclarado ?? 'não declarado'}</span></Item>
            </dl>

            {d.sugestao && (
              <section className="rounded-lg border border-linha">
                <header className="flex flex-wrap items-center justify-between gap-2 border-b border-linha px-4 py-2.5">
                  <h3 className="text-[13px] font-semibold">Sugestão ({d.sugestao.camada === 'DETERMINISTICO' ? 'pela lei' : 'heurística'})</h3>
                  {temSugestao && <Confianca valor={d.sugestao.confianca} />}
                </header>
                <div className="flex flex-col gap-3 px-4 py-3">
                  {temSugestao ? (
                    <PerfilResumo perfil={sugestao as PerfilTributario} nomeCclass={d.sugestao.cclasstribInfo?.nome} />
                  ) : (
                    <p className="text-tinta-2">Sem sugestão automática: o produto precisa de NCM válido nas notas ou de perfil manual.</p>
                  )}
                  {(d.sugestao.alertas as string[]).length > 0 && (
                    <div>
                      <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.06em] text-tinta-3">Pontos de atenção</p>
                      <ul className="flex list-disc flex-col gap-1 pl-5 text-[13px] text-tinta-2">
                        {(d.sugestao.alertas as string[]).map((a, i) => (
                          <li key={i}>{a}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(d.sugestao.alternativas as Alternativa[]).length > 1 && (
                    <div>
                      <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.06em] text-tinta-3">Enquadramentos possíveis na LC 214</p>
                      <ul className="flex flex-col gap-1.5 text-[13px]">
                        {(d.sugestao.alternativas as Alternativa[]).map((a, i) => (
                          <li key={i} className="rounded border border-linha px-2.5 py-1.5">
                            <span className="num font-medium">{a.cClassTrib}</span> · {ROTULO_TRATAMENTO[a.tratamento as Tratamento] ?? a.tratamento}
                            {a.condicional && <span className="ml-1.5"><Selo tom="alerta">depende do produto/adquirente</Selo></span>}
                            <span className="block text-[12px] text-tinta-3">{a.fundamento}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-[11px] text-tinta-3">Base de conhecimento {d.sugestao.versaoKb} · gerada da LC 214/2025 compilada e da tabela oficial de cClassTrib.</p>
                  {podeAprovar && !editando && (
                    <div className="flex flex-wrap gap-2">
                      {temSugestao && (
                        <Botao variante="primario" disabled={aceitar.isPending} onClick={() => aceitar.mutate({ produtoIds: [produtoId] })}>
                          Aceitar sugestão
                        </Botao>
                      )}
                      <Botao onClick={() => setEditando(true)}>{temSugestao ? 'Editar antes de aprovar' : 'Preencher perfil'}</Botao>
                    </div>
                  )}
                  {aceitar.isError && <Aviso>{mensagemDeErro(aceitar.error)}</Aviso>}
                </div>
              </section>
            )}

            {editando && (
              <FormPerfil
                produtoId={produtoId}
                ncmNotas={d.produto.ncmAtual}
                tipo={d.produto.tipo}
                inicial={(temSugestao ? sugestao : vigente ? perfilDoHistorico(vigente) : null) as PerfilTributario | null}
                cancelar={() => setEditando(false)}
                aoSalvar={() => {
                  setEditando(false)
                  aoAprovar()
                  void q.refetch()
                }}
              />
            )}

            {d.historico.length > 0 && (
              <section>
                <h3 className="mb-2 text-[13px] font-semibold">Perfis aprovados</h3>
                <ol className="flex flex-col gap-2">
                  {d.historico.map((h) => (
                    <li key={h.id} className={`rounded-lg border px-3 py-2 text-[13px] ${h === vigente ? 'border-ok/40 bg-ok/5' : 'border-linha'}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="num font-medium">v{h.versao}</span>
                        <span className="num">{h.cClassTrib}</span>
                        <span>{ROTULO_TRATAMENTO[h.tratamento]}</span>
                        <Selo>{h.origem === 'MANUAL' ? 'manual' : h.origem === 'HERDADO' ? 'aplicado em massa' : 'sugestão aceita'}</Selo>
                        {h === vigente && <Selo tom="ok">vigente</Selo>}
                      </div>
                      <p className="mt-1 text-[12px] text-tinta-3">
                        {h.criadoPor} · {dataBr(h.criadoEm)} · base {h.versaoKb}
                      </p>
                      <p className="mt-1 text-[12px] text-tinta-2">{h.fundamento}</p>
                      {h.justificativa && <p className="mt-1 text-[12px] italic text-tinta-2">“{h.justificativa}”</p>}
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  )
}

function Item({ rotulo, largo, children }: { rotulo: string; largo?: boolean; children: React.ReactNode }) {
  return (
    <div className={largo ? 'col-span-2' : ''}>
      <dt className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">{rotulo}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  )
}

function PerfilResumo({ perfil, nomeCclass }: { perfil: PerfilTributario; nomeCclass?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="flex flex-wrap items-center gap-2">
        <span className="num text-base font-semibold">{perfil.cClassTrib}</span>
        <span className="font-medium">{ROTULO_TRATAMENTO[perfil.tratamento]}</span>
        <span className="num text-tinta-3">CST {perfil.cstIbsCbs}</span>
        {perfil.cestaBasicaNacional && <Selo tom="ok">Cesta Básica Nacional</Selo>}
        {perfil.impostoSeletivo.incide && <Selo tom="alerta">Imposto Seletivo</Selo>}
      </p>
      {nomeCclass && <p className="text-[13px] text-tinta-2">{nomeCclass}</p>}
      <p className="text-[13px] text-tinta-2">{perfil.fundamento}</p>
      {perfil.impostoSeletivo.fundamento && <p className="text-[12px] text-tinta-3">{perfil.impostoSeletivo.fundamento}</p>}
      <p className="text-[12px] text-tinta-3">
        Destinação: {ROTULO_DESTINACAO[perfil.destinacao]} · Crédito: {perfil.daCredito ? 'sim' : 'não'}
      </p>
    </div>
  )
}

function perfilDoHistorico(h: {
  ncm: string
  cClassTrib: string
  cstIbsCbs: string
  tratamento: Tratamento
  destinacao: Destinacao
  daCredito: boolean
  isIncide: boolean
  fundamento: string
}): PerfilTributario {
  return {
    ncm: h.ncm,
    ncmEx: null,
    cest: null,
    cClassTrib: h.cClassTrib,
    cstIbsCbs: h.cstIbsCbs,
    tratamento: h.tratamento,
    cestaBasicaNacional: false,
    impostoSeletivo: { incide: h.isIncide, fundamento: null },
    destinacao: h.destinacao,
    daCredito: h.daCredito,
    fundamento: h.fundamento,
  }
}

/** Tratamento coerente com a redução do cClassTrib escolhido (a tabela oficial manda). */
const tratamentoDaReducao = (reducao: number, cst: string): Tratamento =>
  cst === '620' ? 'MONOFASICO' : reducao >= 10000 ? 'ALIQUOTA_ZERO' : reducao >= 6000 ? 'REDUCAO_60' : reducao >= 3000 ? 'REDUCAO_30' : 'INTEGRAL'

function FormPerfil({
  produtoId,
  ncmNotas,
  tipo,
  inicial,
  cancelar,
  aoSalvar,
}: {
  produtoId: string
  ncmNotas: string | null
  tipo: 'SAIDA' | 'ENTRADA'
  inicial: PerfilTributario | null
  cancelar: () => void
  aoSalvar: () => void
}) {
  const trpc = useTRPC()
  const opcoes = useQuery(trpc.produto.opcoesCclasstrib.queryOptions())
  const [p, setP] = useState<PerfilTributario>(
    inicial ?? {
      ncm: ncmNotas ?? '',
      ncmEx: null,
      cest: null,
      cClassTrib: '000001',
      cstIbsCbs: '000',
      tratamento: 'INTEGRAL',
      cestaBasicaNacional: false,
      impostoSeletivo: { incide: false, fundamento: null },
      destinacao: tipo === 'SAIDA' ? 'NAO_APLICAVEL' : 'REVENDA',
      daCredito: tipo === 'ENTRADA',
      fundamento: '',
    },
  )
  const [justificativa, setJustificativa] = useState('')
  const ncmValido = /^\d{8}$/.test(p.ncm)
  const consulta = useQuery({ ...trpc.produto.consultarNcm.queryOptions({ ncm: p.ncm }), enabled: ncmValido })
  const salvar = useMutation(trpc.produto.aplicarPerfil.mutationOptions({ onSuccess: aoSalvar }))
  const mudar = (x: Partial<PerfilTributario>) => setP((a) => ({ ...a, ...x }))

  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-acao/40 bg-acao/[0.03] px-4 py-3"
      onSubmit={(e) => {
        e.preventDefault()
        salvar.mutate({ produtoId, perfil: p, justificativa: justificativa.trim() || undefined })
      }}
    >
      <h3 className="text-[13px] font-semibold">Perfil que será aprovado</h3>
      <div className="grid grid-cols-2 gap-3">
        <Campo rotulo="NCM" dica={ncmValido ? (consulta.data?.existe ? consulta.data.descricao ?? '' : 'NCM não existe na nomenclatura vigente') : '8 dígitos'}>
          <Entrada value={p.ncm} inputMode="numeric" onChange={(e) => mudar({ ncm: e.target.value.replace(/\D/g, '').slice(0, 8) })} required />
        </Campo>
        <Campo rotulo="cClassTrib (tabela oficial)">
          <Selecao
            value={p.cClassTrib}
            onChange={(e) => {
              const o = opcoes.data?.itens.find((x) => x.codigo === e.target.value)
              if (!o) return
              mudar({ cClassTrib: o.codigo, cstIbsCbs: o.cst, tratamento: tratamentoDaReducao(o.reducaoIbs, o.cst), fundamento: p.fundamento || o.fundamento })
            }}
          >
            {opcoes.data?.itens.map((o) => (
              <option key={o.codigo} value={o.codigo}>
                {o.codigo} — {o.nome.slice(0, 70)}
              </option>
            ))}
          </Selecao>
        </Campo>
        <Campo rotulo="Tratamento">
          <Selecao value={p.tratamento} onChange={(e) => mudar({ tratamento: e.target.value as Tratamento })}>
            {TRATAMENTOS.map((t) => (
              <option key={t} value={t}>
                {ROTULO_TRATAMENTO[t]}
              </option>
            ))}
          </Selecao>
        </Campo>
        <Campo rotulo="Destinação">
          <Selecao value={p.destinacao} onChange={(e) => mudar({ destinacao: e.target.value as Destinacao })}>
            {DESTINACOES.map((t) => (
              <option key={t} value={t}>
                {ROTULO_DESTINACAO[t]}
              </option>
            ))}
          </Selecao>
        </Campo>
      </div>
      <div className="flex flex-wrap gap-4 text-[13px]">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={p.cestaBasicaNacional} onChange={(e) => mudar({ cestaBasicaNacional: e.target.checked })} /> Cesta Básica Nacional
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={p.impostoSeletivo.incide} onChange={(e) => mudar({ impostoSeletivo: { ...p.impostoSeletivo, incide: e.target.checked } })} /> Imposto Seletivo
        </label>
        {tipo === 'ENTRADA' && (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={p.daCredito} onChange={(e) => mudar({ daCredito: e.target.checked })} /> Dá crédito
          </label>
        )}
      </div>
      <Campo rotulo="Fundamento legal">
        <Entrada value={p.fundamento} onChange={(e) => mudar({ fundamento: e.target.value })} placeholder="Ex.: LC 214/2025, Art. 135 — Anexo VII, item 3" required />
      </Campo>
      <Campo rotulo="Justificativa (opcional)" dica="Por que o perfil difere da sugestão — fica na trilha de auditoria.">
        <Entrada value={justificativa} onChange={(e) => setJustificativa(e.target.value)} />
      </Campo>
      {salvar.isError && <Aviso>{mensagemDeErro(salvar.error)}</Aviso>}
      <div className="flex gap-2">
        <Botao variante="primario" type="submit" disabled={salvar.isPending || !ncmValido}>
          Aprovar perfil
        </Botao>
        <Botao variante="fantasma" type="button" onClick={cancelar}>
          Cancelar
        </Botao>
      </div>
    </form>
  )
}
